import type { QRL } from "@builder.io/qwik";
import { component$, Slot, useSignal, useVisibleTask$ } from "@builder.io/qwik";
import { NamedIcon } from "./named-icon";
import { cn } from "~/lib/cn";

export interface ReorderableListProps<T extends number> {
  items: T[];
  onReorder$: QRL<(newOrder: T[]) => void>;
  dragHandle?: boolean;
  class?: string;
}

export const ReorderableList = component$(
  <T extends number>({
    items,
    onReorder$,
    dragHandle = true,
    class: className,
  }: ReorderableListProps<T>) => {
    const draggedItem = useSignal<number | null>(null);
    const dragOverItem = useSignal<number | null>(null);
    const listRef = useSignal<HTMLUListElement>();

    // Enable smooth animations when dragging
    // eslint-disable-next-line qwik/no-use-visible-task
    useVisibleTask$(({ cleanup }) => {
      if (!listRef.value) return;

      const observer = new MutationObserver(() => {
        const items = listRef.value?.children;
        if (!items) return;

        Array.from(items).forEach((item) => {
          const el = item as HTMLElement;
          el.style.transition = "transform 0.2s ease";
        });
      });

      observer.observe(listRef.value, { childList: true });
      cleanup(() => observer.disconnect());
    });

    return (
      <ul ref={listRef} class={cn("space-y-2", className)}>
        {items.map((item, index) => (
          <li
            key={item}
            draggable={true}
            class={cn(
              "bg-card relative flex items-center gap-2 rounded-lg border p-3 shadow-sm",
              "hover:border-brand/20 hover:bg-card/80",
              "cursor-grab active:cursor-grabbing",
              draggedItem.value === index && "rounded opacity-50",
              "transition-colors duration-200",
            )}
            onDragStart$={() => (draggedItem.value = index)}
            onDragEnd$={() => {
              if (draggedItem.value === null || dragOverItem.value === null)
                return;

              const newItems = [...items];
              const [reorderedItem] = newItems.splice(draggedItem.value, 1);
              newItems.splice(dragOverItem.value, 0, reorderedItem);

              onReorder$(newItems);
              draggedItem.value = null;
              dragOverItem.value = null;
            }}
            onDragOver$={(e) => {
              e.preventDefault();
              if (draggedItem.value === null) return;

              const rect = (e.target as HTMLElement).getBoundingClientRect();
              const midY = rect.top + rect.height / 2;
              const mouseY = e.clientY;

              dragOverItem.value = index + (mouseY > midY ? 1 : 0);
            }}
          >
            {dragHandle && (
              <div class="cursor-grab active:cursor-grabbing">
                <NamedIcon
                  name="grip-vertical"
                  class="text-muted-foreground/50 h-5 w-5"
                />
              </div>
            )}

            <div class="flex-1">
              <Slot name={`${index}`} />
            </div>
          </li>
        ))}
      </ul>
    );
  },
);
